import { useState } from 'react';
import { Button, Modal } from '@react-apps/design-system';
import { useUpdateFeatureScopes } from '../hooks/use-features';
import { useUpdateRoleScopes } from '../hooks/use-roles';
import { ScopeListInput } from './ScopeListInput';

interface ManageScopesModalProps {
  /** Role e feature são igualmente multi-scope e têm o mesmo endpoint. */
  entity: 'role' | 'feature';
  entityId: string;
  entityName: string;
  initialScopeIds: string[];
  open: boolean;
  onClose: () => void;
}

/**
 * Edita os scopes de uma role (`PUT .../roles/:id/scopes`) ou de uma feature
 * (`PUT .../features/:id/scopes`) — desde a fase 2c as duas são multi-scope. O
 * back exige **pelo menos um** scope (`ArrayNotEmpty`), então salvar fica
 * desabilitado com a lista vazia.
 */
export function ManageScopesModal({
  entity,
  entityId,
  entityName,
  initialScopeIds,
  open,
  onClose,
}: ManageScopesModalProps) {
  const [scopeIds, setScopeIds] = useState<string[]>(initialScopeIds);
  const featureMutation = useUpdateFeatureScopes(entityId);
  const roleMutation = useUpdateRoleScopes(entityId);
  const mutation = entity === 'role' ? roleMutation : featureMutation;

  const close = () => {
    mutation.reset();
    setScopeIds(initialScopeIds);
    onClose();
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    mutation.mutate(scopeIds, { onSuccess: onClose });
  };

  const canSubmit = scopeIds.length > 0 && !mutation.isPending;

  return (
    <Modal
      open={open}
      onClose={close}
      title={`Scopes — ${entityName}`}
      description="Defina em quais scopes este recurso fica disponível. Pelo menos um é obrigatório."
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <ScopeListInput
          label="Scopes"
          values={scopeIds}
          onChange={setScopeIds}
        />
        <p className="text-(--color-text-secondary) text-xs">
          ⚠️ Remover um scope <strong>revoga</strong> as concessões de quem está
          nele.
        </p>
        {mutation.error && (
          <p className="text-sm text-red-600">
            {mutation.error instanceof Error
              ? mutation.error.message
              : 'Falha ao atualizar scopes'}
          </p>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={close}>
            Cancelar
          </Button>
          <Button type="submit" disabled={!canSubmit}>
            {mutation.isPending ? 'Salvando…' : 'Salvar'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
